import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import Reveal from "@/components/SectionReveal";
import { createLead } from "@/lib/api";
import { SERVICES, CITIES, POOL_TYPES, SITE } from "@/data/site";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  city: "",
  pool_type: "",
  service: "",
  message: "",
};

export default function QuoteForm({ presetCity, presetService, compact = false }) {
  const [form, setForm] = useState({
    ...EMPTY,
    city: presetCity || "",
    service: presetService || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Valid phone required";
    if (!form.city) e.city = "Required";
    if (!form.service) e.service = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email || undefined,
        pool_type: form.pool_type || undefined,
        message: form.message || undefined,
        source_path: window.location.pathname,
      };
      await createLead(payload);
      setDone(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
      setForm({ ...EMPTY, city: presetCity || "", service: presetService || "" });
    } catch (err) {
      toast.error("Could not send your request. Please call us at " + SITE.phoneDisplay);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-3xl bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-100 p-10 text-center"
        data-testid="quote-success"
      >
        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h3 className="mt-4 font-heading text-2xl font-bold text-slate-900">Quote request received!</h3>
        <p className="mt-2 text-slate-600">
          A Tampa Bay Pool Pro will reach out shortly. For urgent needs, call us at{" "}
          <a className="font-bold text-sky-700" href={`tel:${SITE.phoneTel}`}>
            {SITE.phoneDisplay}
          </a>
          .
        </p>
        <Button
          type="button"
          variant="outline"
          className="mt-6"
          onClick={() => setDone(false)}
          data-testid="quote-new-request"
        >
          Send another request
        </Button>
      </div>
    );
  }

  return (
    <Reveal testId="quote-form-wrap">
      <form
        onSubmit={onSubmit}
        className={`rounded-3xl bg-white border border-slate-100 shadow-[0_12px_40px_rgba(10,37,64,0.08)] p-6 sm:p-10 grid gap-5 ${
          compact ? "" : "sm:grid-cols-2"
        }`}
        data-testid="quote-form"
      >
        <div className={compact ? "" : "sm:col-span-2"}>
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">Get a Free Quote</p>
          <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900">
            Tell us about your pool — we&apos;ll handle the rest.
          </h3>
        </div>

        <div>
          <Label htmlFor="lead-name">Full name *</Label>
          <Input
            id="lead-name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Jane Smith"
            data-testid="lead-input-name"
            aria-invalid={!!errors.name}
            className="mt-1.5"
          />
          {errors.name && <p className="mt-1 text-xs text-rose-600">{errors.name}</p>}
        </div>

        <div>
          <Label htmlFor="lead-phone">Phone *</Label>
          <Input
            id="lead-phone"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="(727) 555-0123"
            type="tel"
            data-testid="lead-input-phone"
            aria-invalid={!!errors.phone}
            className="mt-1.5"
          />
          {errors.phone && <p className="mt-1 text-xs text-rose-600">{errors.phone}</p>}
        </div>

        <div>
          <Label htmlFor="lead-email">Email</Label>
          <Input
            id="lead-email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="you@email.com"
            type="email"
            data-testid="lead-input-email"
            className="mt-1.5"
          />
        </div>

        <div>
          <Label>City *</Label>
          <Select value={form.city} onValueChange={(v) => set("city", v)}>
            <SelectTrigger className="mt-1.5" data-testid="lead-input-city">
              <SelectValue placeholder="Select your city" />
            </SelectTrigger>
            <SelectContent>
              {CITIES.map((c) => (
                <SelectItem key={c.slug} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
              <SelectItem value="Other Tampa Bay area">Other Tampa Bay area</SelectItem>
            </SelectContent>
          </Select>
          {errors.city && <p className="mt-1 text-xs text-rose-600">{errors.city}</p>}
        </div>

        <div>
          <Label>Pool type</Label>
          <Select value={form.pool_type} onValueChange={(v) => set("pool_type", v)}>
            <SelectTrigger className="mt-1.5" data-testid="lead-input-pool-type">
              <SelectValue placeholder="Select pool type" />
            </SelectTrigger>
            <SelectContent>
              {POOL_TYPES.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={compact ? "" : "sm:col-span-2"}>
          <Label>Service needed *</Label>
          <Select value={form.service} onValueChange={(v) => set("service", v)}>
            <SelectTrigger className="mt-1.5" data-testid="lead-input-service">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s.slug} value={s.title}>
                  {s.title}
                </SelectItem>
              ))}
              <SelectItem value="New Pool System (Pool Systems in a Box)">
                New Pool System (Pool Systems in a Box)
              </SelectItem>
            </SelectContent>
          </Select>
          {errors.service && <p className="mt-1 text-xs text-rose-600">{errors.service}</p>}
        </div>

        <div className={compact ? "" : "sm:col-span-2"}>
          <Label htmlFor="lead-message">Message</Label>
          <Textarea
            id="lead-message"
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            placeholder="Tell us a bit more — pool size, current issue, preferred timing…"
            rows={4}
            data-testid="lead-input-message"
            className="mt-1.5"
          />
        </div>

        <div className={`flex flex-col sm:flex-row sm:items-center gap-3 ${compact ? "" : "sm:col-span-2"}`}>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="lead-submit"
            className="w-full sm:w-auto rounded-full bg-slate-900 hover:bg-sky-700 text-white px-7 py-6 font-bold"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
              </>
            ) : (
              "Get my free quote"
            )}
          </Button>
          <p className="text-xs text-slate-500">
            We&apos;ll never share your info. By submitting, you agree to be contacted by Tampa Bay Pool Pros.
          </p>
        </div>
      </form>
    </Reveal>
  );
}
